import { TEST_IDS } from "@/constants/testIds";
import { useChat } from "@/contexts/ChatContext";
import { cn } from "@/lib/utils";
import { useThreadStore } from "@/store";
import { Reply } from "lucide-react";
import PropTypes from "prop-types";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import {
  AIErrorDisplay,
  CanvasPreview,
  ErrorAssistantMessage,
  ImageEditPreview,
  MemoryIndicator,
  MessageActions,
  MessageAvatar,
  MessageContent,
  MessageErrorBoundary,
  ReasoningBlock,
  SourcesAvatars,
} from "./components";
import { FileAttachments } from "./components/FileAttachments";
import { DalleImageRenderer } from "./components/MessageSkeletons";
import { useCanvasIntegration } from "./hooks/useCanvasIntegration";
import { useMessageState } from "./hooks/useMessageState";
import { parseToolCalls, getImageToolCallId } from "./utils/parsingUtils";
import { extractToolData } from "./utils/toolDataExtractors";
import {
  extractToolErrors,
  getCanvasError,
  getWebSearchError,
} from "./utils/toolErrorUtils";

/**
 * MessageItem - Simplified message component using composable architecture
 * Replaces complex Message.jsx with cleaner grid-based layout
 *
 * @component
 * @example
 * <MessageItem
 *   message={messageObject}
 *   isStreaming={false}
 *   isLastMessage={true}
 *   previousMessage={prevMessageObject}
 *   onEdit={handleEdit}
 * />
 */
const MessageItem = memo(function MessageItem({
  message,
  isStreaming: isStreamingProp = false,
  isLastMessage: _isLastMessage = false,
  previousMessage: _previousMessage = null,
  fileAttachments = null,
  toolData: toolDataProp = null,
  onEdit = null,
  onRerun = null,
  className = "",
}) {
  // Get editing state and sources sidebar state from context
  const {
    isEditingMessage,
    editingMessageId,
    editingMessageContent,
    cancelEditingMessage,
    saveEditedMessage,
    startReplyToMessage,
    sourcesSidebarOpen,
    setSourcesSidebarOpen,
    setActiveSources,
    setDeepResearchSidebarOpen,
    setImageArtifactSidebarOpen,
    retryLastMessage,
    isStreaming: isContextStreaming,
  } = useChat();

  // Get thread's workspace ID for memory indicator navigation
  // Use the thread's workspace_id (where memory was created), not currentWorkspaceId
  // (which is the currently viewed workspace and may differ)
  const { currentThread } = useThreadStore();

  // Check if this message is currently being edited
  const isEditing = isEditingMessage && editingMessageId === message?.id;
  const [editContent, setEditContent] = useState(editingMessageContent || "");
  const editTextareaRef = useRef(null);

  // Consolidate message state from multiple sources
  const {
    effectiveMessage,
    effectiveIsStreaming,
    hasToolCalls,
    hasCompletedToolCalls,
    toolCalls: _toolCalls,
    previousUserMessage,
    previousAssistantMessage,
    effectiveWebSearchData,
    effectiveImageData,
    effectiveCanvasData,
    isDeepResearchMessage: _isDeepResearchMessage,
    effectiveDeepResearchData: _effectiveDeepResearchData,
  } = useMessageState({
    message,
    isStreaming: isStreamingProp,
    allMessages: [], // Will be populated by parent if needed
    toolData: toolDataProp,
  });

  const isUser = effectiveMessage?.role === "user";
  const content = effectiveMessage?.content || "";

  // Strip reply context text from display content if present
  // This handles legacy messages that had context prepended
  const displayContent = content
    // Strip legacy reply context: [Replying to User/Assistant's message: "..."]
    .replace(/^\[Replying to (?:User|Assistant)'s message: ".*?"\]\n\n/s, "")
    // Strip new canvas reply context: [Context: This is a reply to a canvas with the following content:...]
    .replace(
      /^\[Context: This is a reply to a canvas with the following content:\n```[\s\S]*?```\]\n\n/m,
      ""
    )
    // Strip auto-detected canvas context: [Context: Continuing from previous canvas with the following content:...]
    .replace(
      /^\[Context: Continuing from previous canvas with the following content:\n```[\s\S]*?```\]\n\n/m,
      ""
    );

  // Extract reply_to from metadata
  const replyToId = effectiveMessage?.metadata?.reply_to;
  const allMessages = useThreadStore((state) => state.messages);
  const currentThreadId = useThreadStore((state) => state.currentThreadId);

  // Get thread ID from message or fallback to current thread
  const threadId = effectiveMessage?.thread_id || currentThreadId;

  // Try to find the target message in the store
  let replyTargetMessage = replyToId
    ? allMessages.find((m) => m.id === replyToId)
    : null;

  // If not found in store but we have reply_context, use that as fallback
  // This handles the case where the message hasn't loaded yet
  if (
    replyToId &&
    !replyTargetMessage &&
    effectiveMessage?.metadata?.reply_context
  ) {
    const replyContext = effectiveMessage.metadata.reply_context;
    replyTargetMessage = {
      id: replyContext.messageId || replyToId,
      role: replyContext.role,
      content: replyContext.content || replyContext.preview,
    };
  }

  // Check if the reply target has canvas data and extract canvas title
  const replyTargetToolData = replyTargetMessage
    ? extractToolData(replyTargetMessage, false)
    : null;
  const replyTargetCanvasTitle = replyTargetToolData?.canvasData?.name || null;

  // Update edit content when editing starts - use useEffect to avoid updating state during render
  useEffect(() => {
    if (isEditing) {
      setEditContent(editingMessageContent || "");
    }
  }, [isEditing, editingMessageContent]);

  // Handler functions for MessageActions
  const copyToClipboard = useCallback(
    async (textToCopy) => {
      // If no text is provided, fall back to displayContent (cleaned content)
      const finalText = textToCopy || displayContent || "";
      try {
        await navigator.clipboard.writeText(finalText);
        // Could add toast notification here
      } catch (err) {
        console.error("Failed to copy:", err);
      }
    },
    [displayContent]
  );

  // Edit handlers
  const handleInputChange = useCallback((e) => {
    setEditContent(e.target.value);
  }, []);

  const handleEditSubmit = useCallback(async () => {
    if (!editContent.trim()) return;

    // Safety check: Only allow editing user messages
    if (effectiveMessage?.role !== "user") {
      console.error("Cannot edit non-user message:", {
        id: effectiveMessage?.id,
        role: effectiveMessage?.role,
      });
      cancelEditingMessage();
      return;
    }

    try {
      // Save the edited message - this will update the message and trigger a new AI response
      await saveEditedMessage(editContent);
    } catch (error) {
      console.error("Error saving edited message:", error);
      // Don't cancel editing on error so user can retry
    }
  }, [
    editContent,
    effectiveMessage?.role,
    effectiveMessage?.id,
    saveEditedMessage,
    cancelEditingMessage,
    editingMessageId,
    isEditing,
  ]);

  const handleCancelEdit = useCallback(() => {
    cancelEditingMessage();
    setEditContent("");
  }, [cancelEditingMessage]);

  // Reply handler
  const handleReply = useCallback(
    (message) => {
      if (startReplyToMessage) {
        startReplyToMessage(message);
      }
    },
    [startReplyToMessage]
  );

  // Sources click handler
  const handleSourcesClick = useCallback(
    (sources) => {
      // If sources sidebar is already open with the same sources, close it
      if (sourcesSidebarOpen) {
        setSourcesSidebarOpen(false);
        setActiveSources(null);
        return;
      }

      // Close all other sidebars before opening sources sidebar
      setDeepResearchSidebarOpen(false);
      setImageArtifactSidebarOpen(false);

      // Set sources and open sources sidebar
      setActiveSources(sources);
      setSourcesSidebarOpen(true);
    },
    [
      sourcesSidebarOpen,
      setSourcesSidebarOpen,
      setActiveSources,
      setDeepResearchSidebarOpen,
      setImageArtifactSidebarOpen,
    ]
  );

  // Rerun handler
  const handleRerun = useCallback(() => {
    // Safety check: Only allow rerunning user messages
    if (effectiveMessage?.role !== "user") {
      console.error("Cannot rerun non-user message:", {
        id: effectiveMessage?.id,
        role: effectiveMessage?.role,
      });
      return;
    }

    if (onRerun) {
      onRerun();
    }
  }, [onRerun, effectiveMessage?.role, effectiveMessage?.id]);

  // Scroll to referenced message handler
  const scrollToMessage = useCallback((messageId) => {
    const element = document.querySelector(`[data-message-id="${messageId}"]`);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "center" });
      // Optional: add highlight animation
      element.classList.add("highlight-flash");
      setTimeout(() => element.classList.remove("highlight-flash"), 2000);
    }
  }, []);

  // Canvas integration
  const { canvasTitleFromQuery } = useCanvasIntegration(
    isUser,
    effectiveMessage,
    effectiveCanvasData,
    effectiveIsStreaming,
    hasCompletedToolCalls,
    previousUserMessage,
    undefined
  );

  // Use file attachments passed from parent (Messages.jsx extracts from storage_files)
  const messageFileAttachments = fileAttachments || [];
  const hasFileAttachments = messageFileAttachments.length > 0;

  // Reasoning block state
  const reasoningContent =
    effectiveMessage?.metadata?.reasoningContent ||
    effectiveMessage?.reasoningContent ||
    effectiveMessage?.metadata?.reasoning_content;

  const reasoningDuration =
    effectiveMessage?.metadata?.reasoning_seconds ||
    effectiveMessage?.metadata?.reasoningSeconds ||
    effectiveMessage?.metadata?.reasoningDuration ||
    effectiveMessage?.reasoningDuration;

  // For reasoning blocks, override streaming state if reasoning is complete
  // Reasoning is only streaming if the message is streaming AND inReasoningBlock is true
  const reasoningIsStreaming =
    effectiveIsStreaming && effectiveMessage?.metadata?.inReasoningBlock;

  // Claude pattern: Early return for error assistant messages
  // Errors appear as assistant message bubbles with inline retry button
  if (effectiveMessage?.isError && effectiveMessage?.role === "assistant") {
    return (
      <MessageErrorBoundary messageId={effectiveMessage?.id}>
        <div
          id={`message-${effectiveMessage?.id}`}
          className={cn("group relative w-full", "assistant-message", className)}
          data-message-id={effectiveMessage?.id}
          data-role={effectiveMessage?.role}
          data-testid={TEST_IDS.CHAT_MESSAGE_ASSISTANT}
        >
          <div className="grid grid-cols-[auto_1fr] gap-3 sm:gap-4 md:gap-6 items-start">
            <div className="w-8" /> {/* Spacer for avatar alignment */}
            <div className="min-w-0">
              <ErrorAssistantMessage
                message={effectiveMessage}
                onRetry={retryLastMessage}
                isRetrying={isContextStreaming}
                showRetry={_isLastMessage}
              />
            </div>
          </div>
        </div>
      </MessageErrorBoundary>
    );
  }

  // User messages have different layout (right-aligned bubble)
  if (isUser) {
    return (
      <MessageErrorBoundary messageId={effectiveMessage?.id}>
        <div
          id={`message-${effectiveMessage?.id}`}
          className={cn(
            "group relative w-full mb-6",
            "assistant-message",
            className
          )}
          data-message-id={effectiveMessage?.id}
          data-role={effectiveMessage?.role}
          data-testid={TEST_IDS.CHAT_MESSAGE_USER}
        >
          {/* User message wrapper for right alignment */}
          <div
            className={cn(
              "flex w-full pr-3 sm:pr-4",
              isEditing ? "justify-start" : "justify-end"
            )}
          >
            <div
              className={cn(
                "flex flex-col",
                isEditing ? "w-full items-stretch" : "items-end max-w-[70%]"
              )}
            >
              {/* Reply indicator */}
              {replyToId && (
                <div className="mb-2 mr-2 max-w-full">
                  <div className="bg-muted/50 border-l-4 border-primary rounded-r-md px-3 py-2 text-xs">
                    <div className="flex items-center gap-1 text-muted-foreground mb-1">
                      <Reply className="h-3 w-3" />
                      <span className="font-medium">
                        Replying to{" "}
                        {replyTargetCanvasTitle ? (
                          <span className="text-primary">
                            {replyTargetCanvasTitle}
                          </span>
                        ) : replyTargetMessage?.role === "user" ? (
                          "yourself"
                        ) : (
                          "Assistant"
                        )}
                      </span>
                    </div>
                    {replyTargetMessage ? (
                      <>
                        <div className="text-foreground/70 line-clamp-2 mb-1">
                          {replyTargetMessage.content?.substring(0, 150) ||
                            "[No content]"}
                          {replyTargetMessage.content &&
                            replyTargetMessage.content.length > 150 &&
                            "..."}
                        </div>
                        <button
                          onClick={() => scrollToMessage(replyToId)}
                          className="text-primary hover:text-primary/80 underline text-xs"
                        >
                          View message
                        </button>
                      </>
                    ) : (
                      <div className="text-muted-foreground italic">
                        (deleted message)
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Image Edit Preview - Show thumbnail of image being edited */}
              {effectiveMessage?.metadata?.isImageEditRequest &&
                effectiveMessage?.metadata?.imageEditContext && (
                  <ImageEditPreview
                    imageUrl={
                      effectiveMessage.metadata.imageEditContext
                        .originalImageUrl
                    }
                    editPrompt={
                      effectiveMessage.metadata.imageEditContext.editPrompt ||
                      content
                    }
                  />
                )}

              {/* File Attachments - Show user uploaded files above the bubble */}
              {hasFileAttachments && (
                <FileAttachments
                  files={messageFileAttachments}
                  messageId={effectiveMessage?.id}
                  threadId={threadId}
                  isUser={isUser}
                  className="mb-2"
                />
              )}

              {/* User message bubble */}
              <div
                className={cn(
                  "px-3 py-1 sm:px-4 sm:py-1.5 md:px-4 md:py-2",
                  isEditing ? "w-full" : "bg-muted/50 rounded-3xl"
                )}
              >
                <MessageContent
                  displayContent={displayContent}
                  effectiveIsStreaming={effectiveIsStreaming}
                  handleCitationClick={() => {}}
                  isUser={isUser}
                  isEditing={isEditing}
                  editContent={editContent}
                  editTextareaRef={editTextareaRef}
                  handleInputChange={handleInputChange}
                  handleEditSubmit={handleEditSubmit}
                  handleCancelEdit={handleCancelEdit}
                  sources={null}
                  message={effectiveMessage}
                />
              </div>

              {/* Sources Avatars - above action buttons */}
              {effectiveWebSearchData && (
                <SourcesAvatars
                  sources={effectiveWebSearchData}
                  className="mt-2"
                />
              )}

              {/* User message actions below bubble */}
              <MessageActions
                isUser={isUser}
                message={effectiveMessage}
                content={displayContent}
                displayContent={displayContent}
                isStreaming={effectiveIsStreaming}
                showButtons={!effectiveIsStreaming || hasCompletedToolCalls}
                hasToolCalls={hasToolCalls}
                hasCompletedToolCalls={hasCompletedToolCalls}
                hasFileAttachments={hasFileAttachments}
                toolData={{
                  firecrawlSearchData: effectiveWebSearchData,
                  dalleImageData: effectiveImageData,
                  canvasData: effectiveCanvasData,
                }}
                onCopy={copyToClipboard}
                onEdit={onEdit}
                onShare={() => {}}
                onRerun={handleRerun}
                onReply={handleReply}
                onSourcesClick={handleSourcesClick}
                onCanvasClick={undefined}
                shareButtonEnabled={false}
              />
            </div>
          </div>
        </div>
      </MessageErrorBoundary>
    );
  }

  // Assistant messages use grid layout with avatar
  return (
    <MessageErrorBoundary messageId={effectiveMessage?.id}>
      <div
        id={`message-${effectiveMessage?.id}`}
        className={cn("group relative w-full", "assistant-message", className)}
        data-message-id={effectiveMessage?.id}
        data-role={effectiveMessage?.role}
        data-testid={TEST_IDS.CHAT_MESSAGE_ASSISTANT}
      >
        {/* Grid layout: avatar column + content column */}
        <div className="grid grid-cols-[auto_1fr] gap-3 sm:gap-4 md:gap-6 items-start">
          {/* Avatar Column */}
          <MessageAvatar
            isUser={isUser}
            message={effectiveMessage}
            previousMessage={previousAssistantMessage}
            isStreaming={effectiveIsStreaming}
            hasContent={
              Boolean(content && content.trim().length > 0) ||
              Boolean(reasoningContent && reasoningContent.trim().length > 0)
            }
            hasToolCalls={hasToolCalls}
            hasCompletedToolCalls={hasCompletedToolCalls}
            isMobile={false}
            isCanvasOpen={false}
          />

          {/* Content Column */}
          <div className="flex flex-col gap-3 min-w-0">
            {/* Reply indicator */}
            {replyToId && (
              <div className="bg-muted/30 border-l-4 border-primary rounded-r-md px-3 py-2 text-xs max-w-2xl">
                <div className="flex items-center gap-1 text-muted-foreground mb-1">
                  <Reply className="h-3 w-3" />
                  <span className="font-medium">
                    Replying to{" "}
                    {replyTargetCanvasTitle ? (
                      <span className="text-primary">
                        {replyTargetCanvasTitle}
                      </span>
                    ) : replyTargetMessage?.role === "user" ? (
                      "You"
                    ) : (
                      "Assistant"
                    )}
                  </span>
                </div>
                {replyTargetMessage ? (
                  <>
                    <div className="text-foreground/70 line-clamp-2 mb-1">
                      {replyTargetMessage.content?.substring(0, 150) ||
                        "[No content]"}
                      {replyTargetMessage.content &&
                        replyTargetMessage.content.length > 150 &&
                        "..."}
                    </div>
                    <button
                      onClick={() => scrollToMessage(replyToId)}
                      className="text-primary hover:text-primary/80 underline text-xs"
                    >
                      View message
                    </button>
                  </>
                ) : (
                  <div className="text-muted-foreground italic">
                    (deleted message)
                  </div>
                )}
              </div>
            )}

            {/* Reasoning Block (if present) - Production build approach:
                Show if we have reasoning content OR if streaming with inReasoningBlock flag */}
            {((reasoningContent && reasoningContent.trim().length > 0) ||
              (reasoningIsStreaming &&
                effectiveMessage?.metadata?.inReasoningBlock)) && (
              <ReasoningBlock
                content={reasoningContent}
                duration={reasoningDuration}
                isStreaming={reasoningIsStreaming}
                messageId={effectiveMessage?.id}
              />
            )}

            {/* File Attachments */}
            {hasFileAttachments && (
              <FileAttachments
                files={messageFileAttachments}
                messageId={effectiveMessage?.id}
                threadId={threadId}
                isUser={isUser}
              />
            )}

            {/* DALL-E Images */}
            {effectiveImageData && (
              <DalleImageRenderer
                dalleImageData={effectiveImageData}
                messageId={effectiveMessage?.id}
                threadId={threadId}
                toolCallId={getImageToolCallId(parseToolCalls(effectiveMessage))}
                isStreaming={effectiveIsStreaming}
              />
            )}

            {/* Canvas Preview */}
            {effectiveCanvasData && (
              <CanvasPreview
                canvasData={effectiveCanvasData}
                onCanvasClick={undefined}
                messageId={effectiveMessage?.id}
                message={effectiveMessage}
              />
            )}

            {/* Main Message Content (skip if DALL-E image is present) */}
            {content && !effectiveImageData && (
              <MessageContent
                displayContent={displayContent}
                effectiveIsStreaming={effectiveIsStreaming}
                handleCitationClick={() => {}}
                isUser={isUser}
                isEditing={isEditing}
                editContent={editContent}
                editTextareaRef={editTextareaRef}
                handleInputChange={handleInputChange}
                handleEditSubmit={handleEditSubmit}
                handleCancelEdit={handleCancelEdit}
                sources={effectiveWebSearchData}
                message={effectiveMessage}
              />
            )}

            {/* Tool Errors or Sources Avatars - above action buttons */}
            {(() => {
              // Check for web search error first
              if (effectiveWebSearchData) {
                const webSearchError = getWebSearchError(
                  effectiveWebSearchData
                );
                if (webSearchError) {
                  return (
                    <AIErrorDisplay
                      error={webSearchError.error}
                      errorType={webSearchError.errorType}
                      toolName={webSearchError.toolName}
                      className="mt-2"
                    />
                  );
                }
                // No error, show sources
                return (
                  <SourcesAvatars
                    sources={effectiveWebSearchData}
                    className="mt-2"
                  />
                );
              }

              // Check for canvas error
              if (effectiveCanvasData) {
                const canvasError = getCanvasError(effectiveCanvasData);
                if (canvasError) {
                  return (
                    <AIErrorDisplay
                      error={canvasError.error}
                      errorType={canvasError.errorType}
                      toolName={canvasError.toolName}
                      className="mt-2"
                    />
                  );
                }
              }

              // Check for other tool errors in tool calls
              if (hasToolCalls && effectiveMessage?.tool_calls) {
                let parsedToolCalls = effectiveMessage.tool_calls;

                // Parse if string
                if (typeof parsedToolCalls === "string") {
                  try {
                    parsedToolCalls = JSON.parse(parsedToolCalls);
                  } catch {
                    parsedToolCalls = [];
                  }
                }

                if (Array.isArray(parsedToolCalls)) {
                  const toolErrors = extractToolErrors(parsedToolCalls);

                  // Display first tool error (skip web search, canvas, and image generation errors already handled above)
                  const otherErrors = toolErrors.filter(
                    (err) =>
                      err.errorType !== "web_search" &&
                      err.errorType !== "canvas" &&
                      err.errorType !== "image_generation"
                  );

                  if (otherErrors.length > 0) {
                    const firstError = otherErrors[0];
                    return (
                      <AIErrorDisplay
                        error={firstError.error}
                        errorType={firstError.errorType}
                        toolName={firstError.toolName}
                        className="mt-2"
                      />
                    );
                  }
                }
              }

              return null;
            })()}

            {/* Message Actions */}
            <MessageActions
              isUser={isUser}
              message={effectiveMessage}
              content={displayContent}
              displayContent={displayContent}
              isStreaming={effectiveIsStreaming}
              showButtons={
                // Hide buttons for reasoning messages until they have actual content to interact with
                effectiveMessage?.metadata?.isReasoningResponse &&
                !content?.trim()
                  ? false
                  : !effectiveIsStreaming || hasCompletedToolCalls
              }
              hasToolCalls={hasToolCalls}
              hasCompletedToolCalls={hasCompletedToolCalls}
              hasFileAttachments={hasFileAttachments}
              toolData={{
                firecrawlSearchData: effectiveWebSearchData,
                dalleImageData: effectiveImageData,
                canvasData: effectiveCanvasData,
              }}
              onCopy={copyToClipboard}
              onEdit={onEdit}
              onShare={() => {}}
              onRerun={handleRerun}
              onReply={handleReply}
              onSourcesClick={handleSourcesClick}
              onCanvasClick={undefined}
              shareButtonEnabled={false}
            />

            {/* Memory Extraction Indicator - shows when memories were extracted from this message */}
            {effectiveMessage?.metadata?.extractedMemories?.length > 0 && (
              <MemoryIndicator
                memories={effectiveMessage.metadata.extractedMemories}
                extractionTime={effectiveMessage.metadata.memoryExtractionTime}
                workspaceId={currentThread?.workspace_id}
                className="mt-2"
              />
            )}
          </div>
        </div>
      </div>
    </MessageErrorBoundary>
  );
});

MessageItem.propTypes = {
  /** Message object with id, role, content, metadata, tool_calls */
  message: PropTypes.shape({
    id: PropTypes.string.isRequired,
    role: PropTypes.string.isRequired,
    content: PropTypes.string,
    metadata: PropTypes.object,
    tool_calls: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
    file_ids: PropTypes.array,
    reasoningContent: PropTypes.string,
  }).isRequired,

  /** Whether the message is currently streaming */
  isStreaming: PropTypes.bool,

  /** Whether this is the last message in the thread */
  isLastMessage: PropTypes.bool,

  /** Previous message in the thread for context */
  previousMessage: PropTypes.object,

  /** File attachments extracted from message.metadata.storage_files */
  fileAttachments: PropTypes.array,

  /** Pre-extracted tool data from parent component */
  toolData: PropTypes.object,

  /** Callback when edit mode is triggered */
  onEdit: PropTypes.func,

  /** Callback when rerun is triggered after model switch */
  onRerun: PropTypes.func,

  /** Additional CSS classes */
  className: PropTypes.string,
};

export default MessageItem;
